import { differenceInMinutes, isBefore } from 'date-fns'
import { dateFormatter, dateToNow } from 'zens-storybook'

import { Model, ModelData } from '../general'

const LOST_CONNECTIVITY_THRESHOLD = 135 // minutes

export interface Data extends ModelData {
  band?: string
  beacon_2_is_enabled?: boolean
  comments_count?: number
  connectivity_state?: number
  floor_id?: number
  imei?: string
  last_message_at?: string
  last_setting_acknowledged_at?: string
  last_setting_changed_at?: string
  major_1?: number
  major_2?: number
  minor_1?: number
  minor_2?: number
  occupancy_sensor_model?: string
  occupancy_state?: number
  operator?: string
  peripherals_state?: number
  photos_count?: number
  product_type_id?: number
  product_type_name?: string
  serial_number?: string
  sim?: string
  uuid_1?: string
  uuid_2?: string
  workspace_id?: number
  workspace_name?: string
}

export enum ConnectivityState {
  Connected = 0,
  ConnectivityLost = 1,
  Disconnected = 2,
  NeverOnline = 3,
}

export enum PeripheralsState {
  Connected = 0,
  SensorDisconnected = 1,
  LedDisconnected = 2,
  SensorAndLedDisconnected = 3,
  Unknown = 4,
}

export enum OccupancyState {
  Unknown = 0,
  Free = 1,
  Occupied = 2,
}

export enum SensorStatus {
  Free = 0,
  Occupied = 1,
}

export enum Operator {
  Vodafone,
  Telenet,
  Telenor,
  Proximus,
}

export interface Beacon {
  uuid: string
  major: number
  minor: number
}

class Product extends Model<Data> {
  get workspaceId() {
    return this.data.workspace_id
  }

  get workspaceName() {
    return this.data.workspace_name
  }

  get floorId() {
    return this.data.floor_id
  }

  get typeId() {
    return this.data.product_type_id
  }

  get typeName() {
    return this.data.product_type_name
  }

  get serialNumber() {
    return this.data.serial_number
  }

  get connectivityState() {
    switch (this.data.connectivity_state) {
      case 0:
        return ConnectivityState.Connected
      case 1:
        return ConnectivityState.ConnectivityLost
      case 2:
        return ConnectivityState.Disconnected
      case 3:
        return ConnectivityState.NeverOnline
    }
    return undefined
  }

  get peripheralsState() {
    switch (this.data.peripherals_state) {
      case 0:
        return PeripheralsState.Connected
      case 1:
        return PeripheralsState.SensorDisconnected
      case 2:
        return PeripheralsState.LedDisconnected
      case 3:
        return PeripheralsState.SensorAndLedDisconnected
      case 4:
        return PeripheralsState.Unknown
    }
    return undefined
  }

  get occupancyState() {
    switch (this.data.occupancy_state) {
      case 1:
        return SensorStatus.Occupied
      default:
        return SensorStatus.Free
    }
  }

  get displayLastMessageDate() {
    const { i18n } = this.store
    return this.data.last_message_at ? dateFormatter(this.data.last_message_at, 'PPP', i18n.language) : undefined
  }

  get displayLastMessageAgo() {
    const { i18n } = this.store
    return this.data.last_message_at ? dateToNow(this.data.last_message_at, i18n.language) : undefined
  }

  get numberOfComments() {
    return this.data.comments_count || 0
  }
  get numberOfPhotos() {
    return this.data.photos_count || 0
  }
  get numberOfRemarks() {
    return this.numberOfComments + this.numberOfPhotos
  }

  get imei() {
    return this.data.imei
  }

  get sim() {
    return this.data.sim
  }

  get band() {
    return this.data.band
  }

  get operator() {
    switch (this.data.operator) {
      case 'Vodafone':
        return Operator.Vodafone
      case 'Telenet':
        return Operator.Telenet
      case 'Telenor':
        return Operator.Telenor
      case 'Proximus':
        return Operator.Proximus
      default:
        return undefined
    }
  }

  get sensorModel() {
    return this.data.occupancy_sensor_model
  }

  get beacon(): Beacon | undefined {
    if (!this.data.uuid_1 || !this.data.major_1 || !this.data.minor_1) return undefined
    return {
      uuid: this.data.uuid_1,
      major: this.data.major_1,
      minor: this.data.minor_1,
    }
  }

  get secondBeaconEnabled() {
    return !!this.data.beacon_2_is_enabled
  }

  get secondBeacon(): Beacon | undefined {
    if (!this.data.uuid_2 || !this.data.major_2 || !this.data.minor_2) return undefined
    return {
      uuid: this.data.uuid_2,
      major: this.data.major_2,
      minor: this.data.minor_2,
    }
  }

  get syncFailed() {
    const acknowledgedAt = this.acknowledgedAt
    const settingChangedAt = this.settingChangedAt

    return !!(
      this.data.last_setting_changed_at &&
      (!this.data.last_setting_acknowledged_at ||
        !acknowledgedAt ||
        !settingChangedAt ||
        (isBefore(acknowledgedAt, settingChangedAt) &&
          differenceInMinutes(new Date(), settingChangedAt) > LOST_CONNECTIVITY_THRESHOLD))
    )
  }

  get settingChangedAt() {
    return this.data.last_setting_changed_at ? new Date(this.data.last_setting_changed_at) : undefined
  }
  get acknowledgedAt() {
    return this.data.last_setting_acknowledged_at ? new Date(this.data.last_setting_acknowledged_at) : undefined
  }
}

export default Product
